/**
 * @module fx/ShareCard
 * Score-card generator — renders a styled score summary to a canvas
 * and exports it as a PNG data URL for native share / download.
 *
 * Architecture: the generator accepts an injectable {@link CanvasLike}
 * interface so unit tests can pass a lightweight stub without a real DOM.
 *
 * Card layout (640 × 360 px, 16:9 landscape):
 *  - Background: dark gradient (#1a1a2e → #16213e)
 *  - Game title centred at top
 *  - Score in large type
 *  - Mode badge (CLASSIC / ZEN)
 *  - Optional: leaderboard rank
 *  - Branding footer
 */

import type { GameMode } from '../game-config.js';

/** Minimal 2-D rendering context the card needs. */
export interface Canvas2DContext {
  canvas: { width: number; height: number };
  fillStyle: string | CanvasGradient | CanvasPattern;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  globalAlpha: number;
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): CanvasGradient;
  fillRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number, maxWidth?: number): void;
  roundRect?(x: number, y: number, w: number, h: number, r: number): void;
  stroke?(): void;
  beginPath?(): void;
}

/** Canvas-like with toDataURL for export. */
export interface CanvasLike {
  width: number;
  height: number;
  getContext(id: '2d'): Canvas2DContext | null;
  toDataURL(type?: string, quality?: number): string;
}

/** Data needed to render the score card. */
export interface ShareCardData {
  score: number;
  mode: GameMode;
  /** Optional leaderboard rank (1-based), or null if not in top 10. */
  rank: number | null;
}

/** Width of the share card canvas (px). */
const CARD_W = 640;
/** Height of the share card canvas (px). */
const CARD_H = 360;

const TITLE = 'Fruit Slasher';
const FOOTER = 'Play at fruit-slasher.pages.dev';

/**
 * Renders the score card onto the given canvas and returns a PNG data URL.
 *
 * @param canvas - Target canvas (640 × 360 expected; resized if needed).
 * @param data   - Score, mode, and rank data to display.
 * @returns PNG data URL string suitable for `<a href>` download or Web Share API.
 */
export function renderShareCard(canvas: CanvasLike, data: ShareCardData): string {
  canvas.width = CARD_W;
  canvas.height = CARD_H;

  const ctx = canvas.getContext('2d');
  if (ctx === null) return '';

  // Background gradient.
  const grad = ctx.createLinearGradient(0, 0, 0, CARD_H);
  grad.addColorStop(0, '#1a1a2e');
  grad.addColorStop(1, '#16213e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Title.
  ctx.fillStyle = '#e94560';
  ctx.font = 'bold 28px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(TITLE, CARD_W / 2, 32);

  // Score value.
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 96px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(data.score), CARD_W / 2, CARD_H / 2 - 16);

  // Mode badge.
  const modeLabel = data.mode === 'zen' ? 'ZEN' : 'CLASSIC';
  const modeColor = data.mode === 'zen' ? '#4fc3f7' : '#ff7043';
  ctx.fillStyle = modeColor;
  ctx.font = 'bold 20px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(modeLabel, CARD_W / 2, CARD_H / 2 + 56);

  // Rank line (if in top 10).
  if (data.rank !== null) {
    ctx.fillStyle = '#ffd700';
    ctx.font = '18px system-ui, sans-serif';
    ctx.fillText(`#${data.rank} on leaderboard`, CARD_W / 2, CARD_H / 2 + 86);
  }

  // Footer.
  ctx.fillStyle = '#ffffff';
  ctx.globalAlpha = 0.4;
  ctx.font = '14px system-ui, sans-serif';
  ctx.textBaseline = 'bottom';
  ctx.fillText(FOOTER, CARD_W / 2, CARD_H - 20);
  ctx.globalAlpha = 1;

  return canvas.toDataURL('image/png');
}

/**
 * Attempts to share the score card using the Web Share API with file attachment.
 * Falls back to triggering a PNG download if the API is unavailable.
 *
 * @param dataUrl - PNG data URL from {@link renderShareCard}.
 * @param score   - Score value for the filename.
 */
export async function shareOrDownload(dataUrl: string, score: number): Promise<void> {
  const filename = `fruit-slasher-${score}.png`;

  // Attempt Web Share API with file (requires HTTPS + browser support).
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function'
  ) {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `I scored ${score} in Fruit Slasher!`,
          files: [file],
        });
        return;
      }
    } catch {
      // Fall through to download.
    }
  }

  // Fallback: programmatic download anchor.
  if (typeof document !== 'undefined') {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    a.click();
  }
}
